// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import bemify, { toModifiers } from 'bemify-js';

import 'app/common/components/TextInput.scss';

import type { KindType } from 'app/common/types';

// https://www.w3.org/TR/2010/WD-wai-aria-20100916/states_and_properties#aria-labelledby
// If the label text is visible on screen, we SHOULD use aria-labelledby and SHOULD NOT use aria-label. Use aria-label only if the interface is such that it is not possible to have a visible label on the screen. User agents give precedence to aria-labelledby over aria-label when computing the accessible name property.

type Props = {
  className?: string,
  kind?: KindType,
  isMultiLine: boolean,
  fauxReadOnly?: boolean,
  type: string,
  onChangeValue: (value: string, name: string, didConfirm: boolean) => void,
  onChange: (event: SyntheticInputEvent<HTMLInputElement>) => void,
  onBlur: (event: SyntheticInputEvent<HTMLInputElement>) => void,
  inputRef: () => void,
  placeholder?: string,
  'aria-label'?: string,
  'aria-labelledby'?: string,
  'aria-required'?: boolean,
  'aria-invalid'?: boolean,
  'aria-describedby'?: string,
};

export default class TextInput extends Component<Props> {
  static defaultProps = {
    type: 'text',
    isMultiLine: false,
    onBlur: () => {},
    onChange: () => {},
    onChangeValue: () => {},
    inputRef: () => {},
    'aria-required': false,
    'aria-invalid': false,
    'aria-describedby': '',
  };

  onChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
    if (!this.props.fauxReadOnly) {
      const { name, value } = e.target;
      this.props.onChangeValue(value, name, false);
      this.props.onChange(e);
    }
  };

  onBlur = (e: SyntheticInputEvent<HTMLInputElement>) => {
    if (!this.props.fauxReadOnly) {
      const { name, value } = e.target;
      this.props.onChangeValue(value, name, true);
    }
    this.props.onBlur(e);
  };

  render() {
    const { kind, type, ...props } = this.props;
    const bem = bemify('text-input');
    const Tag = props.isMultiLine ? 'textarea' : 'input';

    return (
      <Tag
        ref={props.inputRef}
        type={props.isMultiLine ? undefined : type}
        {..._.omit(
          props,
          'fauxReadOnly',
          'onChangeValue',
          'isMultiLine',
          'inputRef',
          'aria-required',
          'aria-invalid',
          'aria-label',
          'aria-labelledby',
          'aria-describedby'
        )}
        aria-describedby={props['aria-describedby']}
        aria-invalid={props['aria-invalid']}
        aria-label={
          props['aria-labelledby']
            ? undefined
            : props['aria-label'] || props.placeholder
        }
        aria-labelledby={props['aria-labelledby']}
        aria-required={props['aria-required']}
        className={bem(toModifiers(kind), props.className)}
        onBlur={this.onBlur}
        onChange={this.onChange}
      />
    );
  }
}
